import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { validationUtils, urlUtils } from '../utils'
import { ClientErrorView } from '../components/ClientErrorView'
import type { Client, ClientLoadErrorType } from '../types'

export const ForgotPasswordView: React.FC = () => {
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')

  const [client, setClient] = useState<Client | null>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | undefined>()
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingClient, setIsLoadingClient] = useState(true)
  const [isEmailSent, setIsEmailSent] = useState(false)

  useEffect(() => {
    const loadClient = async () => {
      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoadingClient(false)
        return
      }

      try {
        const clientData = await clientApi.getClientByPublicId(clientId)
        setClient(clientData)
      } catch (error) {
        console.error('Error loading client:', error)
        setClientLoadError('client_not_found')
      } finally {
        setIsLoadingClient(false)
      }
    }

    loadClient()
  }, [clientId])

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('L\'email est requis')
      return false
    }
    if (!validationUtils.isValidEmail(email)) {
      setEmailError('Format d\'email invalide')
      return false
    }
    setEmailError(undefined)
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateEmail() || !clientId) return

    setIsLoading(true)
    try {
      const resetUrlPrefix = urlUtils.buildPasswordResetUrlPrefix(clientId)
      await userApi.requestPasswordReset(email, resetUrlPrefix)
      setIsEmailSent(true)
    } catch (error) {
      console.error('Forgot password error:', error)
      setEmailError('Une erreur est survenue, veuillez réessayer plus tard')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingClient) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p className="card-loading-text">Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client) {
    return null
  }

  if (isEmailSent) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          {client.logo && (
            <img
              src={client.logo}
              alt={client.title}
              style={{ height: '60px', marginBottom: '1rem' }}
            />
          )}
          <h1 className="card-title">Email envoyé</h1>
          <p className="card-subtitle">
            Si un compte est associé à <strong>{email}</strong>, vous recevrez un email
            avec un lien pour redéfinir votre mot de passe.
          </p>
        </div>

        <Link
          to={`/login?client_id=${clientId}`}
          className="btn btn-primary btn-full"
        >
          Retour à la connexion
        </Link>
      </div>
    )
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img
            src={client.logo}
            alt={client.title}
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Mot de passe oublié</h1>
        <p className="card-subtitle">
          Saisissez votre adresse email pour recevoir un lien de réinitialisation
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Adresse email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-input ${emailError ? 'shake' : ''}`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (emailError) setEmailError(undefined)
            }}
            placeholder="votre@email.com"
            disabled={isLoading}
            required
          />
          {emailError && (
            <div className="form-error">{emailError}</div>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              Envoi...
            </>
          ) : (
            'Envoyer le lien de réinitialisation'
          )}
        </button>
      </form>

      <div className="card-footer">
        <p className="card-footer-text">
          <Link to={`/login?client_id=${clientId}`} className="link">
            Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  )
}
